import React from 'react';
import API_URL from '../apiUrl';
import './SmsConsent.css';

// Single source of truth for the SMS opt-in disclosure. Every form that
// collects consent renders this component, so the wording a supporter agrees to
// can never drift between pages — which is what The Campaign Registry and the
// carriers check.

export const ORG_NAME = 'Ren Nushaj for MI State Senate';

// Every category of message the campaign sends. The Campaign Registry reviews
// this list, so it must stay in sync with what actually goes out over SMS.
export const MESSAGE_TYPES = [
  'Event invitations and reminders',
  'Fundraising appeals and donation requests',
  'Endorsement announcements',
  'Voter registration reminders and voting information',
  'Volunteer opportunities and recruitment',
  'Campaign news and updates',
];

export const DISCLOSURE_INTRO =
  `By checking this box, I agree to receive SMS text messages from ${ORG_NAME}, including:`;

export const DISCLOSURE_TERMS =
  'Message and data rates may apply. Message frequency varies. Text STOP to cancel. ' +
  'Text HELP for help. Your mobile information, including your phone number and opt-in ' +
  'consent, will not be shared with third parties or affiliates for marketing or ' +
  'promotional purposes.';

// Stored verbatim with the submission so the campaign can show exactly what a
// supporter agreed to if a carrier ever audits the consent record.
export const buildConsentText = () =>
  [
    DISCLOSURE_INTRO,
    ...MESSAGE_TYPES.map((type) => `- ${type}`),
    `${DISCLOSURE_TERMS} View our Privacy Policy: ${window.location.origin}/privacy-policy`,
  ].join('\n');

export const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const isValidUsMobile = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

export async function submitSmsOptIn({
  firstName,
  lastName = '',
  email = '',
  zipCode = '',
  phone,
}) {
  const response = await fetch(`${API_URL}/api/sms-opt-in/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      first_name: firstName,
      last_name: lastName,
      email,
      zip_code: zipCode,
      phone,
      consent: true,
      consent_text: buildConsentText(),
      consent_page: window.location.href,
    }),
  });

  if (!response.ok) {
    // Surface the server's field error (bad number, consent missing) rather
    // than a generic failure.
    const details = await response.json().catch(() => null);
    const firstError = details && Object.values(details).flat()[0];
    throw new Error(typeof firstError === 'string' ? firstError : 'Request failed');
  }
  return response.json();
}

/**
 * The consent checkbox and its disclosure. Must be rendered directly below the
 * phone number field, and must start unchecked.
 *
 * `required` is true only on the dedicated opt-in page, where receiving texts is
 * the point of the form. On forms with another purpose it stays optional, so
 * nobody is pushed into consenting to reach the campaign.
 */
const SmsConsent = ({ id = 'smsConsent', checked, onChange, required = false }) => (
  <div className="sms-consent">
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      required={required}
    />
    <label htmlFor={id}>
      <span className="sms-consent-intro">
        {DISCLOSURE_INTRO}
        {!required && <span className="sms-consent-optional"> (optional)</span>}
      </span>
      <ul className="sms-consent-list">
        {MESSAGE_TYPES.map((type) => (
          <li key={type}>{type}</li>
        ))}
      </ul>
      <span className="sms-consent-terms">
        {DISCLOSURE_TERMS} View our{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        .
      </span>
    </label>
  </div>
);

export default SmsConsent;
